import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  SelectChangeEvent,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import projectService, { Project, NextProjectId } from '../../services/projectService';
import { listUsersForBusinessForms, AppUser } from '../../services/userService';

interface AddProjectFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (project: Project) => void;
  onEdit?: (project: Project) => void;
  onDelete?: (projectId: string) => void;
  editMode?: boolean;
  projectData?: Project | null;
}

const AddProjectForm: React.FC<AddProjectFormProps> = ({ 
  open, 
  onClose, 
  onAdd, 
  onEdit, 
  onDelete, 
  editMode = false, 
  projectData = null 
}) => {
  // Definiujemy interfejs dla danych formularza
  interface FormData {
    name: string;
    clientName: string;
    contractNumber: string;
    agreementNumber: string;
    contractDate: string;
    contractSubject: string;
    investmentAddress: string;
    kzmEmails: string;
    startDate: string;
    endDate: string;
    value: string;
    budget: string;
    firstResponsiblePerson: string;
    secondResponsiblePerson: string;
    responsible_person1?: string; // user_id
    responsible_person2?: string; // user_id
    warrantyPeriod: string;
    driveLink: string;
    miroLink: string;
    projectPhase: string;
    contractDraft1: string;
    contractDraft2: string;
    kzm_draft: string;
    pokr_draft: string;
    pocr_draft: string;
    selectedPackage: string;
    packageDetails: string;
    documentation: string;
    [key: string]: string | undefined; // Sygnatura indeksu dopuszcza undefined dla opcjonalnych pól
  }

  const [formData, setFormData] = useState<FormData>({
    name: '',
    clientName: '',
    contractNumber: '',
    agreementNumber: '',
    contractDate: '',
    contractSubject: '',
    investmentAddress: '',
    kzmEmails: '',
    startDate: '',
    endDate: '',
    value: '',
    budget: '',
    firstResponsiblePerson: '',
    secondResponsiblePerson: '',
    responsible_person1: '',
    responsible_person2: '',
    warrantyPeriod: '',
    driveLink: '',
    miroLink: '',
    projectPhase: 'W REALIZACJI',
    contractDraft1: 'https://docs.google.com/document/d/1Dtb2im-Ca5FdsEQOh7pYQ50a9Jc2upYwL6lccBd3B_Y/edit?tab=t.0',
    contractDraft2: 'https://docs.google.com/document/d/1KBxzXjAWVz_cj83fPxMEICwUn20QL-wySkXSDwQc0NA/edit?tab=t.0',
    kzm_draft: 'https://docs.google.com/document/d/1du1yqIrgC6Hjuz2sd9nU8dI-8dFuFg8ddlA562g4xnM/edit?tab=t.0',
    pokr_draft: 'https://docs.google.com/document/d/1Arpn4Pvs0R-zOGMzclDo94qm6M8MlbTitbfCuajBw_A/edit?tab=t.0',
    pocr_draft: 'https://docs.google.com/document/d/1eyyAh0mRNMqy5uEVE7-jI-8VidhjFxIW9MHAADcTzzE/edit?tab=t.0',
    selectedPackage: '',
    packageDetails: '',
    documentation: ''
  });

  // Stan dla następnego ID projektu
  const [nextProjectId, setNextProjectId] = useState<NextProjectId | null>(null);
  const [loadingNextId, setLoadingNextId] = useState<boolean>(false);
  
  // Stan dla tworzenia folderu projektu
  const [creatingFolder, setCreatingFolder] = useState<boolean>(false);
  const [folderCreated, setFolderCreated] = useState<boolean>(false);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Pobierz następne ID projektu przy otwarciu formularza
  useEffect(() => {
    if (open && !editMode) {
      fetchNextProjectId();
    }
  }, [open, editMode]);

  // Lista użytkowników do dropdownów
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingUsers(true);
        const res = await listUsersForBusinessForms();
        setUsers(res.filter(u => u.is_active));
      } catch (e) {
        console.error('Błąd pobierania użytkowników:', e);
      } finally {
        setLoadingUsers(false);
      }
    };
    if (open) load();
  }, [open]);

  // Funkcja do pobierania następnego ID projektu
  const fetchNextProjectId = async () => {
    try {
      setLoadingNextId(true);
      const nextId = await projectService.getNextProjectId();
      setNextProjectId(nextId);
      
      // Ustaw ID projektu w formularzu
      setFormData(prev => ({
        ...prev,
        contractNumber: nextId.nextIdFormatted
      }));
    } catch (error) {
      console.error('Błąd podczas pobierania następnego ID projektu:', error);
      setSnackbarMessage('Nie udało się pobrać następnego ID projektu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingNextId(false);
    }
  };

  // Funkcja do tworzenia folderu projektu
  const createProjectFolder = async (projectName: string): Promise<{ success: boolean, folder?: any, error?: string }> => {
    if (!projectName) {
      return { success: false, error: 'Nazwa projektu jest wymagana' };
    }

    // Sprawdź, czy nazwa projektu ma odpowiednią długość
    if (projectName.length < 3) {
      return { success: false, error: 'Nazwa projektu musi zawierać co najmniej 3 znaki' };
    }
    
    // Sprawdź, czy nazwa projektu zawiera niedozwolone znaki
    const forbiddenChars = /[<>:"\/\\|?*]/;
    if (forbiddenChars.test(projectName)) {
      return { success: false, error: 'Nazwa projektu zawiera niedozwolone znaki: < > : " / \\ | ? *' };
    }

    try {
      setCreatingFolder(true);
      setFolderError(null);
      
      const result = await projectService.createProjectFolder(projectName);
      
      setFolderCreated(true);
      return { success: true, folder: result.folder };
    } catch (error: any) {
      console.error('Błąd podczas tworzenia folderu projektu:', error);
      
      // Przygotuj bardziej szczegółowy komunikat błędu
      let errorMessage = 'Nie udało się utworzyć folderu projektu';
      
      if (error.response) {
        // Serwer zwrócił odpowiedź z kodem statusu innym niż 2xx
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 403) {
          errorMessage = 'Brak uprawnień do dysku współdzielonego Google Drive';
        } else if (error.response.status === 404) {
          errorMessage = 'Nie znaleziono dysku współdzielonego Google Drive';
        } else if (error.response.status === 503) {
          errorMessage = 'Usługa Google Drive jest tymczasowo niedostępna';
        }
      } else if (error.request) {
        // Żądanie zostało wysłane, ale nie otrzymano odpowiedzi
        errorMessage = 'Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.';
      } else if (error.message) {
        // Coś poszło nie tak podczas konfiguracji żądania
        errorMessage = `Błąd: ${error.message}`;
      }
      
      setFolderError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCreatingFolder(false);
    }
  };

  // Wypełnij formularz danymi projektu, jeśli jest w trybie edycji
  useEffect(() => {
    if (editMode && projectData) {
      setFormData({
        name: projectData.project_name || '',
        clientName: projectData.client_name || '',
        contractNumber: projectData.project_id || '',
        agreementNumber: projectData.contract_number || '',
        contractDate: projectData.contract_sign_date || '',
        contractSubject: projectData.contract_subject || '',
        investmentAddress: projectData.investment_address || '',
        kzmEmails: projectData.kzm_emails || '',
        startDate: projectData.start_date || '',
        endDate: projectData.end_date || '',
        value: projectData.value ? projectData.value.toString() : '',
        budget: projectData.budget ? projectData.budget.toString() : '',
        firstResponsiblePerson: projectData.responsiblePerson1?.display_name || projectData.responsible_person1 || '',
        secondResponsiblePerson: projectData.responsiblePerson2?.display_name || projectData.responsible_person2 || '',
        responsible_person1: projectData.responsible_person1 || projectData.responsiblePerson1?.user_id || '',
        responsible_person2: projectData.responsible_person2 || projectData.responsiblePerson2?.user_id || '',
        warrantyPeriod: projectData.warranty_period_months ? projectData.warranty_period_months.toString() : '',
        driveLink: projectData.google_drive_link || '',
        miroLink: projectData.miro_link || '',
        projectPhase: projectData.project_phase || 'W REALIZACJI',
        contractDraft1: projectData.contract_draft_1 || 'https://docs.google.com/document/d/1Dtb2im-Ca5FdsEQOh7pYQ50a9Jc2upYwL6lccBd3B_Y/edit?tab=t.0',
        contractDraft2: projectData.contract_draft_2 || 'https://docs.google.com/document/d/1KBxzXjAWVz_cj83fPxMEICwUn20QL-wySkXSDwQc0NA/edit?tab=t.0',
        kzm_draft: projectData.kzm_draft || 'https://docs.google.com/document/d/1du1yqIrgC6Hjuz2sd9nU8dI-8dFuFg8ddlA562g4xnM/edit?tab=t.0',
        pokr_draft: projectData.pokr_draft || 'https://docs.google.com/document/d/1Arpn4Pvs0R-zOGMzclDo94qm6M8MlbTitbfCuajBw_A/edit?tab=t.0',
        pocr_draft: projectData.pocr_draft || 'https://docs.google.com/document/d/1eyyAh0mRNMqy5uEVE7-jI-8VidhjFxIW9MHAADcTzzE/edit?tab=t.0',
        selectedPackage: (projectData as any).selectedPackage || '',
        packageDetails: (projectData as any).packageDetails || '',
        documentation: (projectData as any).documentation || ''
      });
    } else {
      // Reset formularza w trybie dodawania
      setFormData({
        name: '',
        clientName: '',
        contractNumber: '',
        agreementNumber: '',
        contractDate: '',
        contractSubject: '',
        investmentAddress: '',
        kzmEmails: '',
        startDate: '',
        endDate: '',
        value: '',
        budget: '',
        firstResponsiblePerson: '',
        secondResponsiblePerson: '',
        responsible_person1: '',
        responsible_person2: '',
        warrantyPeriod: '',
        driveLink: '',
        miroLink: '',
        projectPhase: 'W REALIZACJI',
        contractDraft1: 'https://docs.google.com/document/d/1Dtb2im-Ca5FdsEQOh7pYQ50a9Jc2upYwL6lccBd3B_Y/edit?tab=t.0',
        contractDraft2: 'https://docs.google.com/document/d/1KBxzXjAWVz_cj83fPxMEICwUn20QL-wySkXSDwQc0NA/edit?tab=t.0',
        kzm_draft: 'https://docs.google.com/document/d/1du1yqIrgC6Hjuz2sd9nU8dI-8dFuFg8ddlA562g4xnM/edit?tab=t.0',
        pokr_draft: 'https://docs.google.com/document/d/1Arpn4Pvs0R-zOGMzclDo94qm6M8MlbTitbfCuajBw_A/edit?tab=t.0',
        pocr_draft: 'https://docs.google.com/document/d/1eyyAh0mRNMqy5uEVE7-jI-8VidhjFxIW9MHAADcTzzE/edit?tab=t.0',
        selectedPackage: '',
        packageDetails: '',
        documentation: ''
      });
    }
  }, [editMode, projectData, open]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    const update: Partial<FormData> = { [name]: value } as any;
    if (name === 'responsible_person1') {
      const u = users.find(u => u.user_id === value);
      update.firstResponsiblePerson = u?.display_name || `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.email || '';
      update.responsible_person1 = u?.user_id || '';
    }
    if (name === 'responsible_person2') {
      const u = users.find(u => u.user_id === value);
      update.secondResponsiblePerson = u?.display_name || `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.email || '';
      update.responsible_person2 = u?.user_id || '';
    }
    setFormData({
      ...formData,
      ...update
    });
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Walidacja wymaganych pól
      const requiredFields = [
        { field: 'name', label: 'Nazwa projektu' },
        { field: 'clientName', label: 'Nazwa Zamawiającego' },
        { field: 'agreementNumber', label: 'Numer umowy' },
        { field: 'startDate', label: 'Data rozpoczęcia' },
        { field: 'endDate', label: 'Data zakończenia' }
      ];
      
      const missingFields = requiredFields.filter(field => !formData[field.field]);
      
      if (missingFields.length > 0) {
        setError(`Proszę wypełnić wszystkie wymagane pola: ${missingFields.map(f => f.label).join(', ')}`);
        setLoading(false);
        return;
      }
      
      // Walidacja: różne osoby odpowiedzialne
      if (formData.responsible_person1 && formData.responsible_person2 && formData.responsible_person1 === formData.responsible_person2) {
        setError('Pierwsza i druga osoba odpowiedzialna nie mogą być tą samą osobą');
        setLoading(false);
        return;
      }

      // Walidacja dat
      if (formData.startDate && formData.endDate) {
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        
        if (endDate < startDate) {
          setError('Data zakończenia nie może być wcześniejsza niż data rozpoczęcia');
          setLoading(false);
          return;
        }
      }
      
      // Walidacja wartości liczbowych
      if (formData.value && isNaN(parseFloat(formData.value))) {
        setError('Wartość musi być liczbą');
        setLoading(false);
        return;
      }
      
      if (formData.budget && isNaN(parseFloat(formData.budget))) {
        setError('Budżet musi być liczbą');
        setLoading(false);
        return;
      }
      
      if (formData.warrantyPeriod && isNaN(parseInt(formData.warrantyPeriod))) {
        setError('Okres gwarancji musi być liczbą całkowitą');
        setLoading(false);
        return;
      }
      
      // W trybie edycji nie tworzymy nowego folderu
      if (editMode && onEdit && projectData) {
          // Przygotowanie danych projektu zgodnie z interfejsem Project
          const projectToSave: Project = {
            project_id: formData.contractNumber,
            project_name: formData.name,
            start_date: formData.startDate,
            end_date: formData.endDate,
            value: parseFloat(formData.value) || 0,
            budget: parseFloat(formData.budget) || 0,
            client_name: formData.clientName,
            contract_number: formData.agreementNumber,
            contract_sign_date: formData.contractDate,
            contract_subject: formData.contractSubject,
            investment_address: formData.investmentAddress,
            kzm_emails: formData.kzmEmails,
            responsible_person1: formData.responsible_person1 || '',
            responsible_person2: formData.responsible_person2 || '',
            warranty_period_months: formData.warrantyPeriod ? parseInt(formData.warrantyPeriod) : undefined,
            google_drive_link: formData.driveLink,
            miro_link: formData.miroLink,
            work_completion_deadline: formData.endDate, // Używamy daty końcowej jako terminu zakończenia robót
            project_phase: formData.projectPhase as 'W REALIZACJI' | 'ZAKOŃCZONE W OKRESIE GWARANCJI' | 'ZAKOŃCZONE PO GWARANCJI',
            contract_draft_1: formData.contractDraft1,
            contract_draft_2: formData.contractDraft2,
            kzm_draft: formData.kzm_draft,
            pokr_draft: formData.pokr_draft,
            pocr_draft: formData.pocr_draft
          };

        console.log('Aktualizacja projektu:', projectToSave);

        try {
          // Aktualizacja projektu w bazie danych
          const updatedProject = await projectService.updateProject(projectData.project_id, projectToSave);
          onEdit(updatedProject);
          onClose();
        } catch (apiError: any) {
          console.error('Błąd API podczas aktualizacji projektu:', apiError);
          
          // Obsługa błędów API
          if (apiError.response) {
            // Serwer zwrócił odpowiedź z kodem statusu innym niż 2xx
            const errorMessage = apiError.response.data?.message || apiError.response.data?.error || apiError.message;
            setError(`Błąd serwera: ${errorMessage}`);
          } else if (apiError.request) {
            // Żądanie zostało wysłane, ale nie otrzymano odpowiedzi
            setError('Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.');
          } else {
            // Coś poszło nie tak podczas konfiguracji żądania
            setError(`Błąd: ${apiError.message}`);
          }
        }
      } else {
        // W trybie dodawania najpierw sprawdzamy, czy projekt o danym ID już istnieje
        try {
          // Pobierz następne ID projektu, jeśli nie zostało jeszcze pobrane
          if (!formData.contractNumber) {
            setSnackbarMessage('Pobieranie następnego ID projektu...');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            
            await fetchNextProjectId();
            
            if (!formData.contractNumber) {
              setError('Nie udało się pobrać ID projektu. Spróbuj ponownie.');
              setLoading(false);
              return;
            }
          }
          
          // Sprawdź, czy projekt o danym ID już istnieje
          setSnackbarMessage('Sprawdzanie, czy projekt już istnieje...');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          
          try {
            const existingProject = await projectService.getProjectById(formData.contractNumber);
            
            if (existingProject) {
              setError(`Projekt o ID ${formData.contractNumber} już istnieje. Użyj innego ID lub zaktualizuj istniejący projekt.`);
              setLoading(false);
              return;
            }
          } catch (checkError: any) {
            // Jeśli błąd to 404 (Not Found), to projekt nie istnieje i możemy kontynuować
            if (checkError.response && checkError.response.status === 404) {
              console.log('Projekt nie istnieje, można kontynuować');
            } else {
              console.error('Błąd podczas sprawdzania istnienia projektu:', checkError);
              // Kontynuujemy mimo błędu, zakładając, że projekt nie istnieje
            }
          }
          
          // Teraz tworzymy folder projektu
          setSnackbarMessage('Tworzenie folderu projektu...');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          
          // Utwórz folder projektu
          const folderResult = await createProjectFolder(formData.name);
          
          if (!folderResult.success) {
            setError(folderResult.error || 'Nie udało się utworzyć folderu projektu');
            setLoading(false);
            return;
          }
          
          // Aktualizuj dane formularza z informacjami o utworzonym folderze
          const updatedFormData = {
            ...formData,
            contractNumber: folderResult.folder.projectId,
            driveLink: folderResult.folder.webViewLink,
            contractDraft1: formData.contractDraft1,
            contractDraft2: formData.contractDraft2
          };
          
          // Przygotowanie danych projektu zgodnie z interfejsem Project
          const projectToSave: Project = {
            project_id: updatedFormData.contractNumber,
            project_name: updatedFormData.name,
            start_date: updatedFormData.startDate,
            end_date: updatedFormData.endDate,
            value: parseFloat(updatedFormData.value) || 0,
            budget: parseFloat(updatedFormData.budget) || 0,
            client_name: updatedFormData.clientName,
            contract_number: updatedFormData.agreementNumber,
            contract_sign_date: updatedFormData.contractDate,
            contract_subject: updatedFormData.contractSubject,
            investment_address: updatedFormData.investmentAddress,
            kzm_emails: updatedFormData.kzmEmails,
            responsible_person1: formData.responsible_person1 || '',
            responsible_person2: formData.responsible_person2 || '',
            warranty_period_months: updatedFormData.warrantyPeriod ? parseInt(updatedFormData.warrantyPeriod) : undefined,
            google_drive_link: updatedFormData.driveLink,
            miro_link: updatedFormData.miroLink,
            work_completion_deadline: updatedFormData.endDate, // Używamy daty końcowej jako terminu zakończenia robót
            project_phase: updatedFormData.projectPhase as 'W REALIZACJI' | 'ZAKOŃCZONE W OKRESIE GWARANCJI' | 'ZAKOŃCZONE PO GWARANCJI',
            contract_draft_1: updatedFormData.contractDraft1,
            contract_draft_2: updatedFormData.contractDraft2,
            kzm_draft: updatedFormData.kzm_draft,
            pokr_draft: updatedFormData.pokr_draft,
            pocr_draft: updatedFormData.pocr_draft
          };

          console.log('Zapisywanie projektu:', projectToSave);

          try {
            // Sprawdź jeszcze raz, czy projekt o danym ID już istnieje
            // (mogło się zdarzyć, że został utworzony w międzyczasie)
            let projectExists = false;
            try {
              const existingProject = await projectService.getProjectById(projectToSave.project_id);
              if (existingProject) {
                projectExists = true;
              }
            } catch (checkError: any) {
              // Jeśli błąd to 404 (Not Found), to projekt nie istnieje i możemy kontynuować
              if (checkError.response && checkError.response.status === 404) {
                projectExists = false;
              } else {
                console.error('Błąd podczas sprawdzania istnienia projektu:', checkError);
                // Zakładamy, że projekt nie istnieje
                projectExists = false;
              }
            }
            
            if (projectExists) {
              // Jeśli projekt już istnieje, zaktualizuj go zamiast tworzyć nowy
              console.log('Projekt o ID', projectToSave.project_id, 'już istnieje. Aktualizacja projektu...');
              
              try {
                const updatedProject = await projectService.updateProject(projectToSave.project_id, projectToSave);
                setSnackbarMessage('Projekt został zaktualizowany pomyślnie');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                onAdd(updatedProject);
                onClose();
              } catch (updateError: any) {
                console.error('Błąd API podczas aktualizacji projektu:', updateError);
                
                // Obsługa błędów API
                if (updateError.response) {
                  const errorMessage = updateError.response.data?.message || updateError.response.data?.error || updateError.message;
                  setError(`Błąd serwera podczas aktualizacji: ${errorMessage}`);
                } else if (updateError.request) {
                  setError('Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.');
                } else {
                  setError(`Błąd: ${updateError.message}`);
                }
              }
            } else {
              // Jeśli projekt nie istnieje, utwórz nowy
              console.log('Tworzenie nowego projektu o ID', projectToSave.project_id);
              
              try {
                const newProject = await projectService.createProject(projectToSave);
                setSnackbarMessage('Projekt został utworzony pomyślnie');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                onAdd(newProject);
                onClose();
              } catch (createError: any) {
                console.error('Błąd API podczas tworzenia projektu:', createError);
                
                // Jeśli błąd dotyczy duplikatu klucza, spróbuj zaktualizować projekt
                if (createError.response && 
                    createError.response.status === 409 && 
                    createError.response.data && 
                    (createError.response.data.error === 'duplicate_key' || 
                     createError.response.data.message.includes('już istnieje'))) {
                  
                  console.log('Projekt o ID', projectToSave.project_id, 'już istnieje. Próba aktualizacji...');
                  
                  try {
                    const updatedProject = await projectService.updateProject(projectToSave.project_id, projectToSave);
                    setSnackbarMessage('Projekt został zaktualizowany pomyślnie');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    onAdd(updatedProject);
                    onClose();
                  } catch (updateError: any) {
                    console.error('Błąd API podczas aktualizacji projektu:', updateError);
                    
                    if (updateError.response) {
                      const errorMessage = updateError.response.data?.message || updateError.response.data?.error || updateError.message;
                      setError(`Błąd serwera podczas aktualizacji: ${errorMessage}`);
                    } else if (updateError.request) {
                      setError('Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.');
                    } else {
                      setError(`Błąd: ${updateError.message}`);
                    }
                  }
                } else {
                  // Obsługa innych błędów API
                  if (createError.response) {
                    const errorMessage = createError.response.data?.message || createError.response.data?.error || createError.message;
                    setError(`Błąd serwera: ${errorMessage}`);
                  } else if (createError.request) {
                    setError('Brak odpowiedzi z serwera. Sprawdź połączenie internetowe.');
                  } else {
                    setError(`Błąd: ${createError.message}`);
                  }
                }
              }
            }
          } catch (apiError: any) {
            console.error('Nieoczekiwany błąd API:', apiError);
            setError(`Nieoczekiwany błąd: ${apiError.message}`);
          }
        } catch (err: any) {
          console.error('Nieoczekiwany błąd podczas tworzenia projektu:', err);
          setError(err.message || 'Wystąpił nieoczekiwany błąd podczas tworzenia projektu');
          setLoading(false);
        }
      }
    } catch (err: any) {
      console.error('Nieoczekiwany błąd podczas zapisywania projektu:', err);
      setError(err.message || 'Wystąpił nieoczekiwany błąd podczas zapisywania projektu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (editMode && onDelete && projectData) {
      try {
        setLoading(true);
        setError(null);
        
        // Usunięcie projektu z bazy danych
        await projectService.deleteProject(projectData.project_id);
        onDelete(projectData.project_id);
        onClose();
      } catch (err: any) {
        setError(err.message || 'Wystąpił błąd podczas usuwania projektu');
        console.error('Błąd podczas usuwania projektu:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Funkcja do obliczania liczby dni między datami
  const calculateDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Zakładki (tabs) w panelu
  const [tabValue, setTabValue] = useState(0);
  const [packageTabValue, setPackageTabValue] = useState(0); // Podzakładki w zakładce "Wybór pakietu prac"
  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    // W trybie dodawania zakładka 3 (Wybór pakietu prac) jest ukryta
    // Więc zakładka "Media i linki" ma indeks 3 zamiast 4
    if (!editMode && newValue === 3) {
      // Kliknięto zakładkę "Media i linki" (która ma indeks 3 gdy zakładka 3 jest ukryta)
      setTabValue(4);
    } else {
      setTabValue(newValue);
    }
  };
  useEffect(() => {
    if (open) {
      setTabValue(0);
      setPackageTabValue(0);
    }
  }, [open]);

  // Spójne style pól formularza zgodne z makietą
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    // Usuń czerwoną ramkę dla error state - tylko czerwony tekst helperText
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    // Gdy select jest otwarty (expanded), ramka ma kolor #2020AF
    '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  // Skrót klawiszowy: § (oraz warianty £/±/IntlBackslash) – przełączanie zakładek formularza
  useEffect(() => {
    if (!open) return;
    const options = { capture: true } as AddEventListenerOptions;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const isShortcut =
        event.code === 'IntlBackslash' || event.key === '§' || event.key === '£' || event.key === '±';
      if (!isShortcut) return;
      // Nie działaj w polach edycyjnych
      const target = event.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || (target as any)?.isContentEditable;
      if (isEditable) return;
      event.preventDefault();
      event.stopPropagation();
      try {
        (event as any).stopImmediatePropagation?.();
      } catch {}
      const step = event.shiftKey ? -1 : 1;
      const tabsCount = 5; // Dane podstawowe, Terminy i wartości, Osoby odpowiedzialne, Wybór pakietu prac, Media i linki
      setTabValue(v => (v + step + tabsCount) % tabsCount);
    };
    window.addEventListener('keydown', onKeyDown, options);
    return () => {
      window.removeEventListener('keydown', onKeyDown, options as any);
    };
  }, [open]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
              {editMode ? 'Edytuj projekt' : 'Dodaj projekt'}
            </Typography>
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 30,
                minHeight: 30,
                '& > *': {
                  pointerEvents: 'none',
                },
              }}
            >
              <Box 
                component="img" 
                src="/assets/close.svg" 
                alt="Zamknij" 
                sx={{ 
                  width: 13, 
                  height: 13, 
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                }} 
              />
            </IconButton>
          </Box>
          <Box sx={{ px: 4, mt: '36px' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="standard"
              sx={{ 
                position: 'relative',
                '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
                '& .MuiTabs-scroller': { pl: 0, ml: 0 },
                '& .MuiTabs-flexContainer': { justifyContent: 'space-between' }
              }}
              TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
            >
              <Tab 
                label="Dane podstawowe" 
                sx={{ 
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#020242',
                  minWidth: 'auto',
                  maxWidth: 'none',
                  px: 0,
                  textAlign: 'left',
                  '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                }} 
              />
              <Tab 
                label="Terminy i wartości" 
                sx={{ 
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#020242',
                  minWidth: 'auto',
                  maxWidth: 'none',
                  px: 0,
                  textAlign: 'left',
                  '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                }} 
              />
              <Tab 
                label="Osoby odpowiedzialne" 
                sx={{ 
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#020242',
                  minWidth: 'auto',
                  maxWidth: 'none',
                  px: 0,
                  textAlign: 'left',
                  '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                }} 
              />
              {editMode && (
                <Tab 
                  label="Wybór pakietu prac" 
                  sx={{ 
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    textTransform: 'none',
                    color: '#020242',
                    minWidth: 'auto',
                    maxWidth: 'none',
                    px: 0,
                    textAlign: 'left',
                    '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                  }} 
                />
              )}
              <Tab 
                label="Media i linki" 
                sx={{ 
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  textTransform: 'none',
                  color: '#020242',
                  minWidth: 'auto',
                  maxWidth: 'none',
                  px: 0,
                  textAlign: 'left',
                  '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                }} 
              />
            </Tabs>
          </Box>
          <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
            {tabValue === 0 && (
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                {/* 1. linia: Id projektu | Nazwa projektu */}
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Id projektu" required />
                  <TextField
                    fullWidth
                    name="contractNumber"
                    value={formData.contractNumber}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    helperText={loadingNextId ? "Pobieranie następnego ID..." : "To pole jest wymagane"}
                    error={formData.contractNumber === ''}
                    InputProps={{
                      endAdornment: !editMode && (
                        <InputAdornment position="end">
                          <Tooltip title="Odśwież ID projektu">
                            <IconButton 
                              edge="end" 
                              onClick={fetchNextProjectId}
                              disabled={loadingNextId}
                              size="small"
                            >
                              {loadingNextId ? <CircularProgress size={20} /> : <img src="/assets/icons/autorenew.svg" alt="refresh" style={{ width: 20, height: 20 }} />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                      readOnly: loadingNextId
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Nazwa projektu" required />
                  <TextField
                    fullWidth
                    placeholder="Miejscowość, Nazwa instytucji"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    helperText="To pole jest wymagane"
                    error={formData.name === ''}
                  />
                </Grid>

                {/* 2. linia: Nazwa Zamawiającego na całą szerokość */}
                <Grid item xs={12}>
                  <FieldLabel text="Nazwa zamawiającego" required />
                  <TextField
                    fullWidth
                    placeholder="Nazwa"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    helperText="To pole jest wymagane"
                    error={formData.clientName === ''}
                  />
                </Grid>

                {/* 3. linia: Numer umowy (nowe) | Data podpisania umowy */}
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Numer umowy" required />
                  <TextField
                    fullWidth
                    placeholder="Placeholder"
                    name="agreementNumber"
                    value={formData.agreementNumber}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    helperText="To pole jest wymagane"
                    error={formData.agreementNumber === ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Data podpisania umowy" required />
                  <TextField
                    fullWidth
                    placeholder="rrrr-mm-dd"
                    name="contractDate"
                    type="date"
                    value={formData.contractDate}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-input': {
                        color: formData.contractDate ? '#2D2D32' : '#B9B9B9'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#B9B9B9',
                        opacity: 1
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                        </InputAdornment>
                      )
                    }}
                    helperText="To pole jest wymagane"
                    error={formData.contractDate === ''}
                  />
                </Grid>

                {/* Dalej jak było */}
                <Grid item xs={12}>
                  <FieldLabel text="Przedmiot umowy" />
                  <TextField
                    fullWidth
                    placeholder="Pełny przedmiot umowy"
                    name="contractSubject"
                    value={formData.contractSubject}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      ...textFieldSx, 
                      '& .MuiOutlinedInput-root': { height: 84, backgroundColor: '#fff', borderRadius: '4px', alignItems: 'flex-start' },
                      '& .MuiInputBase-input': { padding: 0 },
                      '& .MuiOutlinedInput-input': { padding: 0 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Adres inwestycji" />
                  <TextField
                    fullWidth
                    placeholder="np. Al. Jerozolimskie 3, 00-495 Warszawa"
                    name="investmentAddress"
                    value={formData.investmentAddress}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Faza projektu" />
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select
                      name="projectPhase"
                      value={formData.projectPhase}
                      onChange={handleSelectChange}
                      displayEmpty
                      renderValue={(val) => {
                        const v = String(val || '');
                        return v ? v : 'Faza projektu';
                      }}
                      IconComponent={(iconProps: any) => (
                        <Box
                          component="img"
                          alt="dropdown"
                          src="/assets/icons/keyboard_arrow_down.svg"
                          {...iconProps}
                          sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                        />
                      )}
                      sx={{
                        '&[aria-expanded="true"] ~ .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
                        '&.Mui-expanded ~ .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
                        '&[aria-expanded="true"]': {
                          '& ~ .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                        }
                      }}
                    >
                      <MenuItem value="W REALIZACJI">W REALIZACJI</MenuItem>
                      <MenuItem value="ZAKOŃCZONE W OKRESIE GWARANCJI">ZAKOŃCZONE W OKRESIE GWARANCJI</MenuItem>
                      <MenuItem value="ZAKOŃCZONE PO GWARANCJI">ZAKOŃCZONE PO GWARANCJI</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* wolne miejsce po przeniesieniu maili KZM */}
                </Grid>
              </Grid>
            )}
            {tabValue === 1 && (
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Początek" required />
                  <TextField
                    fullWidth
                    placeholder="rrrr-mm-dd"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-input': {
                        color: formData.startDate ? '#2D2D32' : '#B9B9B9'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#B9B9B9',
                        opacity: 1
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                        </InputAdornment>
                      )
                    }}
                    helperText="To pole jest wymagane"
                    error={formData.startDate === ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Koniec" required />
                  <TextField
                    fullWidth
                    placeholder="rrrr-mm-dd"
                    name="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={{
                      ...textFieldSx,
                      '& .MuiInputBase-input': {
                        color: formData.endDate ? '#2D2D32' : '#B9B9B9'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#B9B9B9',
                        opacity: 1
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                        </InputAdornment>
                      )
                    }}
                    helperText="To pole jest wymagane"
                    error={formData.endDate === ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Wartość" required />
                  <TextField
                    fullWidth
                    placeholder="np. 1500000,00"
                    name="value"
                    type="number"
                    value={formData.value}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                    }}
                    helperText="To pole jest wymagane"
                    error={formData.value === ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Budżet" required />
                  <TextField
                    fullWidth
                    placeholder="np. 1000000,00"
                    name="budget"
                    type="number"
                    value={formData.budget}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                    }}
                    helperText="To pole jest wymagane"
                    error={formData.budget === ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Okres gwarancji (w miesiącach)" />
                  <TextField
                    fullWidth
                    placeholder="—"
                    name="warrantyPeriod"
                    type="number"
                    value={formData.warrantyPeriod}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            )}
            {tabValue === 2 && (
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Pierwsza osoba odpowiedzialna" />
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select
                      name="responsible_person1"
                      value={formData.responsible_person1 || ''}
                      onChange={handleSelectChange}
                      displayEmpty
                      renderValue={(val) => (val ? (users.find(u=>u.user_id===val)?.display_name || '—') : 'Wybierz z listy')}
                      IconComponent={(iconProps: any) => (
                        <Box
                          component="img"
                          alt="dropdown"
                          src="/assets/icons/keyboard_arrow_down.svg"
                          {...iconProps}
                          sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                        />
                      )}
                      sx={{
                        '& .MuiSelect-select': {
                          color: formData.responsible_person1 ? 'inherit' : '#B9B9B9'
                        },
                        '&[aria-expanded="true"]': {
                          '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                        },
                        '&.Mui-expanded': {
                          '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Brak</em>
                      </MenuItem>
                      {loadingUsers ? (
                        <MenuItem value="" disabled><em>Ładowanie...</em></MenuItem>
                      ) : (
                        users.map(u => (
                          <MenuItem key={u.user_id} value={u.user_id}>{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>W tabeli będą wyświetlane inicjały</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel text="Druga osoba odpowiedzialna" />
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select
                      name="responsible_person2"
                      value={formData.responsible_person2 || ''}
                      onChange={handleSelectChange}
                      displayEmpty
                      renderValue={(val) => (val ? (users.find(u=>u.user_id===val)?.display_name || '—') : 'Wybierz z listy')}
                      IconComponent={(iconProps: any) => (
                        <Box
                          component="img"
                          alt="dropdown"
                          src="/assets/icons/keyboard_arrow_down.svg"
                          {...iconProps}
                          sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                        />
                      )}
                      sx={{
                        '& .MuiSelect-select': {
                          color: formData.responsible_person2 ? 'inherit' : '#B9B9B9'
                        },
                        '&[aria-expanded="true"]': {
                          '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                        },
                        '&.Mui-expanded': {
                          '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Brak</em>
                      </MenuItem>
                      {loadingUsers ? (
                        <MenuItem value="" disabled><em>Ładowanie...</em></MenuItem>
                      ) : (
                        users.map(u => (
                          <MenuItem key={u.user_id} value={u.user_id}>{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>W tabeli będą wyświetlane inicjały</Typography>
                </Grid>
              </Grid>
            )}
            {tabValue === 3 && editMode && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Tabs 
                    value={packageTabValue} 
                    onChange={(e, newValue) => setPackageTabValue(newValue)} 
                    variant="standard"
                    sx={{ 
                      position: 'relative',
                      '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
                      '& .MuiTabs-scroller': { pl: 0, ml: 0 },
                      '& .MuiTabs-flexContainer': { justifyContent: 'space-between' }
                    }}
                    TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
                  >
                    <Tab 
                      label="Wybór pakietu prac" 
                      sx={{ 
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        textTransform: 'none',
                        color: '#020242',
                        minWidth: 'auto',
                        maxWidth: 'none',
                        px: 0,
                        textAlign: 'left',
                        '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                      }} 
                    />
                    <Tab 
                      label="Szczegóły pakietu" 
                      sx={{ 
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        textTransform: 'none',
                        color: '#020242',
                        minWidth: 'auto',
                        maxWidth: 'none',
                        px: 0,
                        textAlign: 'left',
                        '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                      }} 
                    />
                    <Tab 
                      label="Dokumentacja" 
                      sx={{ 
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 400,
                        fontSize: '16px',
                        textTransform: 'none',
                        color: '#020242',
                        minWidth: 'auto',
                        maxWidth: 'none',
                        px: 0,
                        textAlign: 'left',
                        '&.Mui-selected': { fontWeight: 500, color: '#020242' }
                      }} 
                    />
                  </Tabs>
                </Box>
                {packageTabValue === 0 && (
                  <Grid container rowSpacing={1.5} columnSpacing={3}>
                    <Grid item xs={12}>
                      <FieldLabel text="Wybór pakietu prac" />
                      <FormControl fullWidth size="small" sx={selectSx}>
                        <Select
                          name="selectedPackage"
                          value={formData.selectedPackage || ''}
                          onChange={handleSelectChange}
                          displayEmpty
                          renderValue={(val) => val || 'Wybierz pakiet prac'}
                          IconComponent={(iconProps: any) => (
                            <Box
                              component="img"
                              alt="dropdown"
                              src="/assets/icons/keyboard_arrow_down.svg"
                              {...iconProps}
                              sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                            />
                          )}
                          sx={{
                            '& .MuiSelect-select': {
                              color: formData.selectedPackage ? 'inherit' : '#B9B9B9'
                            },
                            '&[aria-expanded="true"]': {
                              '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                            },
                            '&.Mui-expanded': {
                              '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Brak</em>
                          </MenuItem>
                          <MenuItem value="PRZYGOTOWANIE">PRZYGOTOWANIE</MenuItem>
                          <MenuItem value="STAN SUROWY">STAN SUROWY</MenuItem>
                          <MenuItem value="STAN DEWELOPERSKI">STAN DEWELOPERSKI</MenuItem>
                          <MenuItem value="WYKOŃCZENIE">WYKOŃCZENIE</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
                {packageTabValue === 1 && (
                  <Grid container rowSpacing={1.5} columnSpacing={3}>
                    <Grid item xs={12}>
                      <FieldLabel text="Szczegóły pakietu" />
                      <TextField
                        fullWidth
                        name="packageDetails"
                        value={formData.packageDetails}
                        onChange={handleChange}
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        placeholder="Wpisz szczegóły pakietu prac"
                        multiline
                        rows={4}
                      />
                    </Grid>
                  </Grid>
                )}
                {packageTabValue === 2 && (
                  <Grid container rowSpacing={1.5} columnSpacing={3}>
                    <Grid item xs={12}>
                      <FieldLabel text="Dokumentacja" />
                      <TextField
                        fullWidth
                        name="documentation"
                        value={formData.documentation}
                        onChange={handleChange}
                        variant="outlined"
                        size="small"
                        sx={textFieldSx}
                        placeholder="Link do dokumentacji lub opis"
                        multiline
                        rows={6}
                        helperText="Możesz dodać link do dokumentacji lub wpisać opis dokumentacji projektu"
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}
            {tabValue === 4 && (
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                <Grid item xs={12}>
                  <FieldLabel text="Google drive" />
                  <TextField
                    fullWidth
                    name="driveLink"
                    value={formData.driveLink}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                    disabled={!editMode}
                    helperText={
                      editMode 
                        ? "Link do folderu projektu na Google Drive" 
                        : "Link do folderu projektu zostanie wygenerowany automatycznie"
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Link do Collectifile" />
                  <TextField
                    fullWidth
                    name="miroLink"
                    value={formData.miroLink}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Adresy email do wysyłki KZM" />
                  <TextField
                    fullWidth
                    name="kzmEmails"
                    value={formData.kzmEmails}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                    helperText="Wprowadź adresy e-mail oddzielone przecinkami"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Szablon umowy 1" />
                  <TextField
                    fullWidth
                    name="contractDraft1"
                    value={formData.contractDraft1}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Szablon umowy 2" />
                  <TextField
                    fullWidth
                    name="contractDraft2"
                    value={formData.contractDraft2}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Szablon KZM" />
                  <TextField
                    fullWidth
                    name="kzm_draft"
                    value={formData.kzm_draft}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Szablon POKR" />
                  <TextField
                    fullWidth
                    name="pokr_draft"
                    value={formData.pokr_draft}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Szablon POCR" />
                  <TextField
                    fullWidth
                    name="pocr_draft"
                    value={formData.pocr_draft}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    sx={textFieldSx}
                    placeholder="Wpisz adres url"
                  />
                </Grid>
              </Grid>
            )}
            {error && (
              <Box sx={{ mt: 2, color: 'error.main', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
          <Divider />
          <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
            <Box>
              {editMode && onDelete && (
                <Button 
                  onClick={handleDelete} 
                  variant="outlined" 
                  color="error"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Usuń projekt'}
                </Button>
              )}
            </Box>
            <Box>
              <Button 
                onClick={onClose} 
                variant="text" 
                disabled={loading}
              >
                Anuluj
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                disabled={loading}
                sx={{ 
                  backgroundColor: '#020242',
                  '&:hover': {
                    backgroundColor: '#030360'
                  }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : (editMode ? 'Zapisz zmiany' : 'Dodaj projekt')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddProjectForm;
